import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid';
import type { ComponentType } from 'react';

export const TRANSACTIONAL_NOTIFICATION_TYPE = {
  id: 'transactional',
  label: 'Transactional',
  description:
    'Fire when a record matches conditions at creation time, with optional follow-ups.',
  icon: NotificationsActiveIcon,
  color: '#2196f3',
} as const;

export type AdvancedNotificationType = {
  id: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string; sx?: object }>;
  workflowType: string;
  color: string;
};

export const ADVANCED_NOTIFICATION_TYPES: AdvancedNotificationType[] = [
  {
    id: 'machineDowntime',
    label: 'Machine Downtime',
    description:
      'Alert when a machine has no running activity for a configured period, with escalation tiers.',
    icon: ScheduleIcon,
    workflowType: 'checkMachineDowntime',
    color: '#ff9800',
  },
  {
    id: 'resourceCommunicationLost',
    label: 'Communication Lost',
    description:
      'Alert when an HMI device has lost communication with a resource for a configured period, with escalation tiers.',
    icon: WifiOffIcon,
    workflowType: 'checkResourceCommunicationLost',
    color: '#e91e63',
  },
];

export const MACHINE_DOWNTIME_WORKFLOW = 'checkMachineDowntime';
export const RESOURCE_COMMUNICATION_LOST_WORKFLOW =
  'checkResourceCommunicationLost';

export function sanitizeScheduleName(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '').slice(0, 40);
}

export type AdvancedNotificationRecipient = {
  recipientType: string;
  recipientMethod: string;
  recipientID: string;
};

export type AdvancedNotificationTier = {
  thresholdMins: number;
  repeatEveryMins: number;
  recipients: AdvancedNotificationRecipient[];
};

export type MachineDowntimeAlertConfig = {
  description: string;
  resourceIDs: string[];
  resourceGroupIDs: string[];
  idleReasonCodes: string[];
  requireProductionJob: boolean;
  notifyOnResolved: boolean;
  resolvedRecipients: AdvancedNotificationRecipient[];
  includePartInformation: boolean;
  pollIntervalMins: number;
  tiers: AdvancedNotificationTier[];
};

export type ResourceCommunicationLostConfig = {
  description: string;
  resourceIDs: string[];
  resourceGroupIDs: string[];
  notifyOnResolved: boolean;
  resolvedRecipients: AdvancedNotificationRecipient[];
  pollIntervalMins: number;
  tiers: AdvancedNotificationTier[];
};

export function createDefaultDowntimeConfig(): MachineDowntimeAlertConfig {
  return {
    description: '',
    resourceIDs: [],
    resourceGroupIDs: [],
    idleReasonCodes: [],
    requireProductionJob: false,
    notifyOnResolved: true,
    resolvedRecipients: [],
    includePartInformation: true,
    pollIntervalMins: 1,
    tiers: [
      {
        thresholdMins: 15,
        repeatEveryMins: 0,
        recipients: [],
      },
    ],
  };
}

export function createDefaultResourceCommunicationLostConfig(): ResourceCommunicationLostConfig {
  return {
    description: '',
    resourceIDs: [],
    resourceGroupIDs: [],
    notifyOnResolved: true,
    resolvedRecipients: [],
    pollIntervalMins: 1,
    tiers: [
      {
        thresholdMins: 15,
        repeatEveryMins: 0,
        recipients: [],
      },
    ],
  };
}

export function normalizeResourceCommunicationLostConfig(
  value?: Partial<ResourceCommunicationLostConfig> | null
): ResourceCommunicationLostConfig {
  const defaults = createDefaultResourceCommunicationLostConfig();
  const config = value || {};
  return {
    ...defaults,
    ...config,
    resourceIDs: Array.isArray(config.resourceIDs) ? config.resourceIDs : [],
    resourceGroupIDs: Array.isArray(config.resourceGroupIDs)
      ? config.resourceGroupIDs
      : [],
    resolvedRecipients: Array.isArray(config.resolvedRecipients)
      ? config.resolvedRecipients
      : [],
    tiers:
      Array.isArray(config.tiers) && config.tiers.length > 0
        ? config.tiers
        : defaults.tiers,
  };
}

export function rowMatchesGlobalSearch(
  row: Record<string, unknown>,
  globalSearch: string,
  searchFields: string[] = ['description', 'itemType']
): boolean {
  if (!globalSearch) {
    return true;
  }

  const searchLower = globalSearch.toLowerCase();
  return searchFields.some((field) => {
    const value = row[field];
    return value != null && String(value).toLowerCase().includes(searchLower);
  });
}

function normalizeFilterValue(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

function rowMatchesFilterItem(
  row: Record<string, unknown>,
  filter: GridFilterItem
): boolean {
  const cell = normalizeFilterValue(row[filter.field as string]);
  const filterValue = normalizeFilterValue(filter.value);

  switch (filter.operator) {
    case 'equals':
      return cell === filterValue;
    case 'contains':
      return cell.toLowerCase().includes(filterValue.toLowerCase());
    case 'startsWith':
      return cell.toLowerCase().startsWith(filterValue.toLowerCase());
    case 'endsWith':
      return cell.toLowerCase().endsWith(filterValue.toLowerCase());
    case 'isEmpty':
      return cell === '';
    case 'isNotEmpty':
      return cell !== '';
    case 'isAnyOf': {
      const values = Array.isArray(filter.value)
        ? filter.value.map(normalizeFilterValue)
        : filterValue
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
      return values.includes(cell);
    }
    default:
      return false;
  }
}

export function rowMatchesGridFilters(
  row: Record<string, unknown>,
  filterItems: GridFilterItem[] = []
): boolean {
  if (!filterItems.length) {
    return true;
  }

  return filterItems.every((filter) => rowMatchesFilterItem(row, filter));
}

function compareSortValues(left: unknown, right: unknown): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  return String(left).localeCompare(String(right), undefined, {
    sensitivity: 'base',
  });
}

export function sortNotificationRows(
  rows: Record<string, unknown>[],
  sortModel: GridSortModel = []
): Record<string, unknown>[] {
  if (!sortModel.length) {
    return rows;
  }

  const sortedRows = [...rows];
  sortedRows.sort((left, right) => {
    for (const sort of sortModel) {
      const comparison = compareSortValues(left[sort.field], right[sort.field]);
      if (comparison !== 0) {
        return sort.sort === 'desc' ? -comparison : comparison;
      }
    }
    return 0;
  });
  return sortedRows;
}

export function mapScheduleToNotificationRow(schedule: {
  scheduleId: string;
  scheduleName: string;
  workflowType: string;
  paused: boolean;
  options?: unknown[];
}): Record<string, unknown> | null {
  const advancedType = ADVANCED_NOTIFICATION_TYPES.find(
    (type) => type.workflowType === schedule.workflowType
  );
  if (!advancedType) {
    return null;
  }

  const config = (schedule.options?.[0] || {}) as Record<string, unknown>;
  const normalizedConfig =
    schedule.workflowType === MACHINE_DOWNTIME_WORKFLOW
      ? {
          ...createDefaultDowntimeConfig(),
          ...config,
          idleReasonCodes: Array.isArray(config.idleReasonCodes)
            ? config.idleReasonCodes
            : [],
          includePartInformation: config.includePartInformation !== false,
          resolvedRecipients: Array.isArray(config.resolvedRecipients)
            ? config.resolvedRecipients
            : [],
        }
      : normalizeResourceCommunicationLostConfig(
          config as Partial<ResourceCommunicationLostConfig>
        );

  return {
    id: `scheduled:${schedule.scheduleId}`,
    scheduleId: schedule.scheduleId,
    scheduleName: schedule.scheduleName,
    notificationKind: advancedType.id,
    description: normalizedConfig.description || schedule.scheduleName,
    active: !schedule.paused,
    notificationTypeLabel: advancedType.label,
    ...(schedule.workflowType === MACHINE_DOWNTIME_WORKFLOW
      ? { downtimeConfig: normalizedConfig }
      : { communicationLostConfig: normalizedConfig }),
    paused: schedule.paused,
  };
}

export function isAdvancedNotificationRow(
  row: Record<string, unknown>
): boolean {
  return ADVANCED_NOTIFICATION_TYPES.some(
    (type) => type.id === row.notificationKind
  );
}

export function getNotificationRowId(
  row: Record<string, unknown>
): string | number {
  if (isAdvancedNotificationRow(row)) {
    return `scheduled:${String(row.scheduleId)}`;
  }
  return typeof row.notificationID === 'number'
    ? row.notificationID
    : String(row.notificationID);
}

function validateAdvancedTiers(
  tiers: AdvancedNotificationTier[]
): string | null {
  if (!tiers.length) {
    return 'At least one escalation tier is required';
  }

  for (let i = 0; i < tiers.length; i += 1) {
    const tier = tiers[i];
    if (!tier.thresholdMins || tier.thresholdMins <= 0) {
      return `Tier ${i + 1} threshold must be greater than zero`;
    }
    if (tier.repeatEveryMins < 0) {
      return `Tier ${i + 1} repeat interval cannot be negative`;
    }
    if (i > 0 && tier.thresholdMins <= tiers[i - 1].thresholdMins) {
      return 'Tier thresholds must be strictly increasing';
    }
    if (!tier.recipients.length) {
      return `Tier ${i + 1} requires at least one recipient`;
    }
    for (
      let recipientIndex = 0;
      recipientIndex < tier.recipients.length;
      recipientIndex += 1
    ) {
      if (!tier.recipients[recipientIndex].recipientID?.trim()) {
        return `Tier ${i + 1}, recipient ${
          recipientIndex + 1
        } requires a selected target`;
      }
    }
  }

  return null;
}

function validateResolvedRecipients(config: {
  notifyOnResolved: boolean;
  resolvedRecipients: AdvancedNotificationRecipient[];
}): string | null {
  if (!config.notifyOnResolved) {
    return null;
  }
  const missingIndex = config.resolvedRecipients.findIndex(
    (recipient) => !recipient.recipientID?.trim()
  );
  return missingIndex === -1
    ? null
    : `Recovery recipient ${missingIndex + 1} requires a selected target`;
}

export function validateDowntimeConfig(
  config: MachineDowntimeAlertConfig
): string | null {
  if (!config.description?.trim()) {
    return 'Description is required';
  }
  if (!config.resourceIDs.length && !config.resourceGroupIDs.length) {
    return 'Select at least one machine or resource group';
  }
  if (!config.pollIntervalMins || config.pollIntervalMins <= 0) {
    return 'Poll interval must be greater than zero';
  }

  return (
    validateAdvancedTiers(config.tiers) || validateResolvedRecipients(config)
  );
}

export function validateResourceCommunicationLostConfig(
  config: ResourceCommunicationLostConfig
): string | null {
  if (!config.description?.trim()) {
    return 'Description is required';
  }
  if (!config.resourceIDs.length && !config.resourceGroupIDs.length) {
    return 'Select at least one resource or resource group';
  }
  if (!config.pollIntervalMins || config.pollIntervalMins <= 0) {
    return 'Poll interval must be greater than zero';
  }

  return (
    validateAdvancedTiers(config.tiers) || validateResolvedRecipients(config)
  );
}
